/* The relative effect chart is used to show the effect that one or more SNPs have on a person's likelihood of having a
particular trait. It is designed to handle both situations where there is a proven numerical effect, and where a study
refers to e.g. 'a small effect' */

<template>
   <!-- Higher / lower gradient line and labels -->
   <div class="flex items-end justify-between pb-2 uppercase" :aria-hidden="true">
      <div class="flex w-1/2 items-end pr-3">
         <span class="text-bolder pr-2 leading-none">-</span>
         <span class="text-lighter block text-sm leading-none">{{ labels.left }}</span>
      </div>
      <div class="grow md:w-1/5"></div>
      <div class="flex w-1/2 items-end justify-end pl-3 text-right">
         <span class="text-lighter block text-sm leading-none">{{ labels.right }}</span>
         <span class="text-bolder pl-2 leading-none">+</span>
      </div>
   </div>
   <div class="relative h-3 w-full" :class="{ 'mt-1': chartRsidKeys.length === 1 }">
      <div
         class="absolute inset-0 rounded-full"
         :style="`background: linear-gradient(270deg, currentcolor, transparent 125%);`"
      ></div>
   </div>

   <!-- If this chart is showing only one SNP, we use a simple marker to show where the effect of that SNP sits on the scale -->
   <!-- [theme class css in cms-themes.scss] -->
   <div
      v-if="chartRsidKeys.length === 1 && !!(chartUserRsidPositions[0] || chartUserRsidPositions[0] === 0)"
      :data-cy="`chartSnp-${chartRsidKeys[0]}`"
   >
      <div class="relative" style="left: 50%; width: calc(50% - 4.5rem)">
         <div
            :class="`absolute w-2.5 h-6 top-0 border-2 rounded-md theme-${color}`"
            :style="`left: ${(chartUserRsidPositions[0] / scale) * 100}%; margin-top: -1.125rem;`"
         ></div>
      </div>
      <!-- In this case we'll also show the more detailed description of the effect of this snp -->
      <div class="flex items-center pt-4">
         <div class="-mt-px flex items-center">
            <div class="relative h-14 w-6 shrink-0">
               <div class="absolute h-8 w-full border-b border-current" style="top: -2px"></div>
               <span class="sr-only">{{
                  $t("dna-testing.trait_reports.aria_at_rsid", { rsid: chartRsidKeys[0] })
               }}</span>
               <span class="text-bolder relative block break-words text-center text-2xl leading-tight">
                  {{ chartUserRsidValues[0].variant }}
               </span>
            </div>
            <div aria-hidden="true" class="relative -mt-1 flex h-12 w-16 shrink-0 items-center">
               <IconHexagon class="absolute left-0 top-0" />
               <span class="text-bolder relative mx-auto mt-2 block w-9 break-words text-center text-xs leading-none">{{
                  chartRsidKeys[0]
               }}</span>
            </div>
         </div>
         <p class="pl-4 leading-snug">{{ reportSnpInfo[chartRsidKeys[0]][chartUserRsidValues[0].variant] }}</p>
      </div>
   </div>
   <!-- Where there are mulitple SNPs on one chart, each is shown by a small icon with the rsid and variant at that position -->
   <div v-else class="relative">
      <div
         class="absolute left-0 top-0 w-1/2 border-r border-dashed border-current opacity-25"
         style="height: calc(100% + 0.5rem)"
      ></div>
      <div v-for="(rsid, index) in chartRsidKeys" :key="rsid">
         <div v-if="!!chartUserRsidPositions[index] || chartUserRsidPositions[index] === 0" class="snp-row">
            <div
               class="snp-block"
               :data-cy="`chartSnp-${chartRsidKeys[index]}`"
               :class="`theme-${color}`"
               :style="`left: ${(chartUserRsidPositions[index] / scale) * 100}%;`"
            >
               <div class="relative h-14 w-6 shrink-0" :class="{ 'order-3': chartUserRsidPositions[index] < 0 }">
                  <div class="absolute h-8 w-full border-b border-current" style="top: -2px"></div>
                  <span class="sr-only">{{ $t("dna-testing.trait_reports.aria_at_rsid", { rsid: rsid }) }}</span>
                  <span class="text-bolder relative block break-words text-center text-2xl leading-tight">
                     {{ chartUserRsidValues[index].variant }}
                  </span>
               </div>
               <div aria-hidden="true" class="relative -mt-1 flex h-12 w-16 shrink-0 items-center">
                  <IconHexagon class="absolute left-0 top-0" />
                  <span
                     class="text-bolder relative mx-auto mt-2 block w-9 break-words text-center text-xs leading-none"
                     >{{ rsid }}</span
                  >
               </div>
               <!-- Directional arrows (where position is not zero.) Zero means this SNP makes this trait neither more or less likely. -->
               <div
                  v-if="chartUserRsidPositions[index] !== 0"
                  class="relative flex w-14 shrink-0 items-center"
                  :class="{ 'order-first': chartUserRsidPositions[index] < 0 }"
               >
                  <div class="relative flex h-8 w-12 items-center justify-center">
                     <div class="absolute top-0 mt-4 h-0 w-12 border-b border-current"></div>
                     <!-- Visually show the exact value (e.g. 2x, 2-3x) if this exists (otherwise use screenreader-only to describe the chart.) -->
                     <!-- If 2 or more SNPs per chart have estimated (s/m/l) rather than numerical effects though, don't visually show numbers on that chart as this will make less sense -->
                     <span
                        v-if="
                           noOfEstimatedPositions > 1 ||
                           ['small', 'medium', 'large'].includes(chartUserRsidValues[index].effectSize.toString())
                        "
                        class="sr-only"
                     >
                        {{ $t("dna-testing.trait_reports.effect") }}
                        {{ chartUserRsidValues[index].effectSize === "small" ? "slightly" : "" }}
                        {{ chartUserRsidPositions[index] < 0 ? labels.left : labels.right }}
                     </span>
                     <div
                        v-else
                        class="text-bolder relative z-10 flex items-center justify-center px-px text-sm"
                        :class="`theme-${color}`"
                     >
                        <span class="sr-only">{{ $t("dna-testing.trait_reports.effect") }} </span>
                        <!-- it may be an 'up to X% effect. To be added when we bring across a report which uses 'up to X' -->
                        <!-- <span v-if="up_to_x[index]">up to </span> -->
                        <!-- ensure screenreader says e.g. X to X where we show X - X -->
                        <span aria-hidden="true">{{ chartUserRsidValues[index].effectSize }}</span>
                        <span class="sr-only">{{
                           chartUserRsidValues[index].effectSize.toString().replace("-", " to ")
                        }}</span>
                        <!-- it may be a '2 times' or a '63 percent' effect -->
                        <span
                           v-if="chartUserRsidValues[index].isPercentageEffect"
                           class="relative text-xxs"
                           style="top: 1px"
                           aria-hidden="true"
                        >
                           %
                        </span>
                        <span v-else aria-hidden="true">x</span>
                        <span class="sr-only">
                           <span v-if="chartUserRsidValues[index].isPercentageEffect">
                              {{ $t("dna-testing.trait_reports.percent") }}
                           </span>
                           <span v-else> {{ $t("dna-testing.trait_reports.times") }} </span>
                           {{ chartUserRsidPositions[index] < 0 ? labels.left : labels.right }}
                        </span>
                     </div>
                  </div>
                  <div
                     aria-hidden="true"
                     class="mt-px w-2 shrink-0"
                     :class="{ 'order-first': chartUserRsidPositions[index] < 0 }"
                     :style="`transform: rotate(${chartUserRsidPositions[index] < 0 ? '180deg' : '0deg'})`"
                  >
                     <IconTriangle />
                  </div>
               </div>
            </div>
         </div>
      </div>
   </div>
   <!-- Note about any missing SNPs -->
   <div v-if="missingSnpRsids.length > 0" data-cy="missing-snps-explainer">
      <p class="py-2 text-sm">
         <span v-for="(rsid, i) in missingSnpRsids" :key="`missing-rsid-${rsid}`" class="text-bolder">
            <span>{{ rsid }}</span
            ><span v-if="i < missingSnpRsids.length - 1">, </span><span v-else>: </span>
         </span>
         <span v-if="missingSnpRsids.length === 1">{{ $t("dna-testing.trait_reports.missing_snp") }}</span>
         <span v-else>{{ $t("dna-testing.trait_reports.missing_snps") }}</span>
         <!-- <span>&nbsp;{{ $t("dna-testing.trait_reports.mising_snp_reason") }}</span> -->
      </p>
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";

const { t: $t } = useI18n();

interface variantInfo {
   variant: string;
   effectSize: number | string /* "small" | "medium" | "large" | "2-3" */;
   isPercentageEffect?: boolean;
   direction: -1 | 0 | 1;
}

interface snpChartContent {
   labelMinus1?: string;
   label_1?: string;
   rsids: {
      [key: string]: variantInfo[];
   };
}

// --------------- Props and state
const state = reactive({
   /* The chart will show one or several variants along a scale. For some variants, research has shown a numerical effect,
   (4x more/less likely or 80% more/less likely), and for others research will only refer to e.g. a small effect.
   Often, one of a certain allele will have a small effect, and having two of that allele will have a larger effect.
   In non-numerical cases, we'll estimate the position of the snp icon, but no number will be displayed. An effect size of
   zero means no effect, and here 1 represents the biggest effect we could see. These values will be multiplied by the scale
   of the chart the snp appears on, which is determined by the biggest numerical effect on that chart. */
   languageToVisual: { small: 0.3, medium: 0.6, large: 0.9 },
});

const props = defineProps<{
   content: snpChartContent;
   userVariants: { [key: string /* RSID */]: string /* Variant e.g. AA */ };
   color: string;
   reportSnpInfo: {
      [key: string /* RSID */]: {
         [key: string /* Variant e.g. TT */]: string /* Detailed description of variant effect */;
      };
   };
}>();

// --------------- Computed properties
const labels = computed(() => {
   return {
      left: props.content?.labelMinus1 || "Lower",
      right: props.content?.label_1 || "Higher",
   };
});

const chartRsidKeys = computed(() => {
   return Object.keys(props.content.rsids);
});

/* To put all of these variants onto a scale of a suitable size, we need to know the biggest numerical effect size that could appear on this chart */
const scale = computed(() => {
   let highestNumericalVal = 1;

   // Loop through each rsid in the chart, and then through each of the variant possibilities for that rsid.
   for (let i = 0; i < chartRsidKeys.value.length; i++) {
      const thisRsid = chartRsidKeys.value[i];
      const rsidVariantsArray = props.content.rsids[thisRsid];
      // Each variant possibility should have an 'effectSize'
      for (let possibility = 0; possibility < rsidVariantsArray.length; possibility++) {
         const thisEffectSize = rsidVariantsArray[possibility].effectSize;

         // Loop through any numerical values that we have and find the highest to use as a scale measure
         if (typeof thisEffectSize === "number" && thisEffectSize > highestNumericalVal) {
            highestNumericalVal = thisEffectSize;
         } else if (typeof thisEffectSize === "string" && thisEffectSize.includes("-")) {
            // Handle e.g. '2-3' times (in this case we'd use 2.5 for positioning, but the label would still say '2-3' for accuracy)
            const numericalEffectSize = returnMiddleOfRange(thisEffectSize);
            if (numericalEffectSize > highestNumericalVal) highestNumericalVal = numericalEffectSize;
         }
      }
   }

   // Use something slightly larger than the highest numerical value we have for the top of the scale
   return highestNumericalVal * 1.25;
});

const chartUserRsidValues = computed(() => {
   // Return an array that can be looped through for each rsid in this chart
   const valuesArray: Array<variantInfo | undefined> = chartRsidKeys.value.map((rsid) => {
      const thisUserVariant = props.userVariants[rsid];

      // Where we have a user variant for that rsid, the value should be the matching option from content.rsids[thisVariant]
      if (thisUserVariant) return props.content.rsids[rsid].find((v) => v.variant === thisUserVariant);
      // Where we do not, the value should be undefined
      else return undefined;
   });
   return valuesArray;
});

const chartUserRsidPositions = computed(() => {
   // For each snp that appears on the chart, we need to know where it should be positioned
   return chartUserRsidValues.value.map((val) => {
      if (typeof val?.effectSize === "number") return val.effectSize * val.direction;
      else if (typeof val?.effectSize === "string" && val.effectSize.includes("-")) {
         const numericalEffectSize = returnMiddleOfRange(val.effectSize);
         return numericalEffectSize * val.direction;
      } else if (["small", "medium", "large"].includes(val?.effectSize)) {
         // Relative values must be used to give an idea of how large the effect is of this variant compared to others in the chart
         return parseFloat(state.languageToVisual[val.effectSize]) * scale.value * val.direction;
      }
      return undefined;
   });
});

const noOfEstimatedPositions = computed(() => {
   /* Different ways of measuring effect sizes - percentages versus 'X times' should never appear on the same chart.
   Numerical effect sizes will also only be shown on charts where no more than one variant position is extrapolated from s/m/l,
   as this could look confusing, and since the research numbers will always appear in more detail later in the report, the chart
   only needs to give an illustration of the relative effects of different snps. So we need to know how many estimated values are in this chart */
   const estimatedValues = chartUserRsidValues.value.filter((rsidVal) =>
      ["small", "medium", "large"].includes(rsidVal?.effectSize.toString())
   );
   return estimatedValues.length;
});

const missingSnpRsids = computed(() => {
   // Return a list of the rsids where we do not have a value for this participant
   return chartUserRsidValues.value
      .map((val, index) => {
         return !val?.variant ? chartRsidKeys.value[index] : null;
      })
      .filter((val) => !!val);
});

// --------------- Methods

const returnMiddleOfRange = (valueRangeString: string) => {
   // E.g. 'this gene is associated with a 3-6x increased risk'. Return the middle value (e.g. 4.5)
   const firstNumber = parseFloat(valueRangeString.split("-")[0]);
   const secondNumber = parseFloat(valueRangeString.split("-")[1]);

   if (!isNaN(firstNumber) && !isNaN(secondNumber)) return firstNumber + (secondNumber - firstNumber) * 0.5;
   else return 0;
};
</script>

<style scoped lang="scss">
.snp-row {
   @apply relative h-16 mt-1;
   left: 50%;
   width: calc(50% - 4.5rem);
}

.snp-block {
   @apply absolute top-0 flex justify-center items-center mt-2;
   transform: translateX(-50%);
   width: 9rem;
}
</style>
